import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { GridService } from '../../services/grid.service';
import { GridColumn, GridCustomAction } from '../../models/grid.model';
import { PaginatorComponent } from '../paginator/paginator.component';
import { PhonePipe } from '../../pipes/phone.pipe';
import { CpfPipe } from '../../pipes/cpf.pipe';

@Component({
  selector: 'app-data-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PaginatorComponent],
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss'],
})
export class DataGridComponent<T = unknown> implements OnInit, OnChanges {
  /** API path (e.g. 'units', 'cost-types') — must expose /grid and /export */
  @Input() path = '';
  /** Column definitions */
  @Input() columns: GridColumn[] = [];
  /** Initial page size */
  @Input() pageSize = 10;
  /** Default sort: e.g. ['id|desc'] */
  @Input() defaultSort: string[] = ['id|desc'];
  /** Route prefix for edit link (e.g. '/units') — clicking Edit navigates to /{prefix}/{id}/edit */
  @Input() editRoutePrefix = '';
  /** Empty state message */
  @Input() emptyMessage = 'Nenhum registro encontrado.';
  /**
   * Serialised filter string (JSON array of GridFilter).
   * When this changes the grid reloads from page 0.
   */
  @Input() filters = '';

  /** Whether to show the edit action button/link (default true) */
  @Input() showEditAction = true;
  /** Whether to show the delete action button (default true) */
  @Input() showDeleteAction = true;

  /** Emits when edit icon is clicked (use instead of editRoutePrefix for inline editing) */
  @Output() editRow = new EventEmitter<T>();
  /** Emits when delete icon is clicked */
  @Output() deleteRow = new EventEmitter<T>();
  /** Custom actions to show in actions column */
  @Input() customActions: GridCustomAction[] = [];
  /** Emits when a custom action is clicked */
  @Output() customActionClick = new EventEmitter<{ actionId: string; row: T }>();
  /** Fields that support inline editing on click */
  @Input() inlineFields: string[] = [];
  /** Emits {row, field, value} when an inline edit is committed */
  @Output() inlineSave = new EventEmitter<{ row: T; field: string; value: unknown }>();

  data: T[] = [];
  totalElements = 0;
  totalPages = 0;
  page = 0;
  sort: string[] = [];
  loading = false;
  error: string | null = null;

  editingCell: { rowId: unknown; field: string } | null = null;
  editingValue: unknown = null;

  private loadSub?: Subscription;

  constructor(private gridService: GridService) {}

  ngOnInit(): void {
    this.sort = [...this.defaultSort];
    this.loadGrid();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload when path or filters change (skip first run — handled by ngOnInit)
    if ((changes['path'] || changes['filters']) && !changes['path']?.firstChange) {
      this.page = 0;
      this.loadGrid();
    }
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.page = 0;
      this.loadGrid();
    }
  }

  loadGrid(): void {
    if (!this.path) return;
    this.loading = true;
    this.error = null;
    this.loadSub?.unsubscribe();
    this.loadSub = this.gridService
      .grid<T>(this.path, this.page, this.pageSize, this.sort, this.filters || undefined)
      .subscribe({
        next: res => {
          // Suporta Spring Page (content) e formato alternativo (data)
          this.data = res.content ?? res.data ?? [];
          this.totalElements = res.totalElements ?? 0;
          this.totalPages = res.totalPages ?? 0;
          this.loading = false;
        },
        error: err => {
          this.error = err?.error?.message || err?.message || 'Falha ao carregar dados.';
          this.loading = false;
        },
      });
  }

  /** Public method so parent can force a reload (e.g. after create/delete). */
  reload(): void {
    this.page = 0;
    this.loadGrid();
  }

  /** Trigger CSV/XLSX export via browser download. */
  exportData(type: 'csv' | 'xlsx' = 'csv'): void {
    this.gridService.export(this.path, type, this.columns, this.filters || undefined);
  }

  onPageChange(p: number): void {
    this.page = p;
    this.loadGrid();
  }

  onRecordsPerPageChange(size: number): void {
    this.pageSize = size;
    this.page = 0;
    this.loadGrid();
  }

  changeSort(column: GridColumn): void {
    if (!column.sortable) return;
    const field = column.valuePath ?? column.field;
    const current = this.sort.find(s => s.startsWith(field + '|'));
    let next: string;
    if (!current) {
      next = `${field}|asc`;
    } else if (current.endsWith('|asc')) {
      next = `${field}|desc`;
    } else {
      next = `${field}|asc`;
    }
    this.sort = [next];
    this.page = 0;
    this.loadGrid();
  }

  getSortMode(column: GridColumn): 'asc' | 'desc' | null {
    const field = column.valuePath ?? column.field;
    const s = this.sort.find(x => x.startsWith(field + '|'));
    if (!s) return null;
    return s.endsWith('|desc') ? 'desc' : 'asc';
  }

  getMobileSortableColumns(): GridColumn[] {
    return this.columns.filter(col => col.sortable);
  }

  getMobileSortField(): string {
    const current = this.sort[0];
    if (!current) return '';
    return current.split('|')[0] ?? '';
  }

  getMobileSortDirection(): 'asc' | 'desc' {
    return this.sort[0]?.endsWith('|asc') ? 'asc' : 'desc';
  }

  changeMobileSort(field: string): void {
    const column = this.columns.find(col => (col.valuePath ?? col.field) === field);
    if (!column?.sortable) return;
    const direction = this.getMobileSortField() === field ? this.getMobileSortDirection() : 'asc';
    this.sort = [field + '|' + direction];
    this.page = 0;
    this.loadGrid();
  }

  toggleMobileSortDirection(): void {
    const field = this.getMobileSortField();
    if (!field) return;
    const nextDirection = this.getMobileSortDirection() === 'asc' ? 'desc' : 'asc';
    this.sort = [field + '|' + nextDirection];
    this.page = 0;
    this.loadGrid();
  }

  getValue(row: T, column: GridColumn): unknown {
    const path = column.valuePath ?? column.field;
    return path.split('.').reduce(
      (o: unknown, k) => (o != null && typeof o === 'object' ? (o as Record<string, unknown>)[k] : null),
      row as object
    );
  }

  formatValue(value: unknown, column: GridColumn): string {
    if (value == null) return '—';
    if (column.format === 'badge') {
      return column.labelMap?.[String(value)] ?? String(value);
    }
    if (column.format === 'boolean') return value ? 'Sim' : 'Não';
    if (column.format === 'currency' && typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    }
    if (column.format === 'number' && typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR').format(value);
    }
    if (column.format === 'date' && (typeof value === 'string' || value instanceof Date)) {
      if (typeof value === 'string') {
        // Plain date yyyy-MM-dd: parse components directly to avoid UTC→local timezone shift
        const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      }
      const d = value instanceof Date ? value : new Date(value);
      return d.toLocaleDateString('pt-BR');
    }
    if (column.format === 'phone') return new PhonePipe().transform(String(value));
    if (column.format === 'cpf') return new CpfPipe().transform(String(value));
    if (column.labelMap) return column.labelMap[String(value)] ?? String(value);
    return String(value);
  }

  getMobileTitleColumn(): GridColumn | null {
    const explicit = this.columns.find(col => col.mobileRole === 'title');
    if (explicit) return explicit;
    return this.columns.find(col =>
      col.mobileRole !== 'hidden' &&
      col.field !== 'id' &&
      col.format !== 'badge'
    ) ?? this.columns.find(col => col.mobileRole !== 'hidden') ?? null;
  }

  getMobileBadgeColumn(): GridColumn | null {
    const explicit = this.columns.find(col => col.mobileRole === 'badge');
    if (explicit) return explicit;
    return this.columns.find(col =>
      col.mobileRole !== 'hidden' &&
      col.format === 'badge'
    ) ?? null;
  }

  getMobileColumnsByRole(role: 'title' | 'badge' | 'field' | 'hidden'): GridColumn[] {
    return this.columns.filter(col => col.mobileRole === role);
  }

  getMobileFieldColumns(): GridColumn[] {
    const titleColumn = this.getMobileTitleColumn();
    const badgeColumn = this.getMobileBadgeColumn();
    return this.columns
      .filter(col =>
        col.mobileRole !== 'hidden' &&
        col !== titleColumn &&
        col !== badgeColumn
      )
      .sort((a, b) => this.getMobileColumnOrder(a) - this.getMobileColumnOrder(b));
  }

  getMobileLabel(column: GridColumn): string {
    return column.mobileLabel ?? column.header;
  }

  private getMobileColumnOrder(column: GridColumn): number {
    return column.mobileOrder ?? this.columns.indexOf(column);
  }

  getRowId(row: T): unknown {
    return row != null && typeof row === 'object' && 'id' in row
      ? (row as Record<string, unknown>)['id']
      : undefined;
  }

  hasCustomActions(): boolean {
    return this.customActions.length > 0;
  }

  hasEditAction(): boolean {
    return this.showEditAction;
  }

  hasDeleteAction(): boolean {
    return this.showDeleteAction;
  }

  hasActions(): boolean {
    return this.hasCustomActions() || this.hasEditAction() || this.hasDeleteAction();
  }

  usesEditRoute(): boolean {
    return !!this.editRoutePrefix;
  }

  usesEditEmitter(): boolean {
    return this.hasEditAction() && !this.usesEditRoute();
  }

  onEdit(row: T, event: Event): void {
    event.stopPropagation();
    this.editRow.emit(row);
  }

  onDelete(row: T, event: Event): void {
    event.stopPropagation();
    this.deleteRow.emit(row);
  }

  onCustomAction(actionId: string, row: T, event: Event): void {
    event.stopPropagation();
    this.customActionClick.emit({ actionId, row });
  }

  isInlineField(col: GridColumn): boolean {
    return this.inlineFields.includes(col.field);
  }

  isEditingCell(row: T, col: GridColumn): boolean {
    return (
      this.editingCell?.rowId === this.getRowId(row) &&
      this.editingCell?.field === col.field
    );
  }

  startInlineEdit(row: T, col: GridColumn, event: Event): void {
    event.stopPropagation();
    this.editingCell = { rowId: this.getRowId(row), field: col.field };
    this.editingValue = this.getValue(row, col);
  }

  commitInlineEdit(row: T, col: GridColumn): void {
    if (!this.editingCell) return;
    this.inlineSave.emit({ row, field: col.field, value: this.editingValue });
    this.editingCell = null;
    this.editingValue = null;
  }

  cancelInlineEdit(): void {
    this.editingCell = null;
    this.editingValue = null;
  }
}
